/**
 * The story a reel is written from. Plain data: no Firebase and no fetch.
 * Lines are what the viewer reads. Shots stay off the Content list.
 */

export type ReelPictureKind = 'still' | 'moving';
export type ReelBeatRole = 'hook' | 'build' | 'turn' | 'payoff';

export interface ReelBeat {
  index: number;
  role: ReelBeatRole;
  /** What the viewer reads. Also the page name. */
  line: string;
  /** Picture prompt. Hidden on the Content list. */
  shot: string;
}

export interface ReelTreatment {
  title: string;
  hook: string;
  tone: string;
  /** Copied onto every shot so the clips match. */
  visualStyle: string;
  /** still = one SDXL frame + slow move. moving = short clips. */
  picture: ReelPictureKind;
  beats: ReelBeat[];
}

const BEAT_ROLES: readonly ReelBeatRole[] = ['hook', 'build', 'turn', 'payoff'];

/** Three empty lines so the panel has somewhere to type before a story exists. */
export function emptyTreatment(): ReelTreatment {
  const roles: ReelBeatRole[] = ['hook', 'build', 'payoff'];
  return {
    title: '',
    hook: '',
    tone: '',
    visualStyle: '',
    picture: 'still',
    beats: roles.map((role, i) => ({
      index: i + 1,
      role,
      line: '',
      shot: '',
    })),
  };
}

/** On-screen lines, in scene order. */
export function lineTitles(treatment: ReelTreatment): string[] {
  return treatment.beats.map((beat) => beat.line);
}

/** Pull a JSON object out of a model reply, including a fenced block. */
export function parseJsonObject(text: string): Record<string, unknown> | null {
  const trimmed = text.trim();
  const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/);
  const raw = fenced ? fenced[1].trim() : trimmed;
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    return value as Record<string, unknown>;
  } catch {
    return null;
  }
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function asRole(value: unknown): ReelBeatRole | null {
  return typeof value === 'string' && (BEAT_ROLES as readonly string[]).includes(value)
    ? (value as ReelBeatRole)
    : null;
}

function asPicture(value: unknown): ReelPictureKind {
  return value === 'moving' ? 'moving' : 'still';
}

function beatCountOk(count: number): boolean {
  return count >= 3 && count <= 5;
}

/**
 * Director reply: title, style, and beat roles. Lines and shots start empty.
 * A missing or unknown picture becomes still.
 */
export function parseDirector(text: string): ReelTreatment | null {
  const value = parseJsonObject(text);
  if (!value) return null;
  const visualStyle = asString(value.visualStyle);
  if (!visualStyle) return null;
  if (!Array.isArray(value.beats) || !beatCountOk(value.beats.length)) return null;

  const beats: ReelBeat[] = [];
  for (let i = 0; i < value.beats.length; i += 1) {
    const item = value.beats[i];
    if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
    const record = item as Record<string, unknown>;
    const role = asRole(record.role);
    if (!role) return null;
    beats.push({
      index: i + 1,
      role,
      line: '',
      shot: '',
    });
  }

  return {
    title: asString(value.title),
    hook: asString(value.hook),
    tone: asString(value.tone),
    visualStyle,
    picture: asPicture(value.picture),
    beats,
  };
}

/** Writer reply: one line per beat. Shots are left alone. */
export function mergeWriterLines(treatment: ReelTreatment, text: string): ReelTreatment | null {
  const value = parseJsonObject(text);
  if (!value || !Array.isArray(value.beats)) return null;
  const lines = new Map<number, string>();
  for (const item of value.beats) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
    const record = item as Record<string, unknown>;
    const index = typeof record.index === 'number' ? record.index : Number(record.index);
    const line = asString(record.line);
    if (!Number.isInteger(index) || !line) return null;
    lines.set(index, line);
  }
  if (treatment.beats.some((beat) => !lines.has(beat.index))) return null;
  return {
    ...treatment,
    beats: treatment.beats.map((beat) => ({
      ...beat,
      line: lines.get(beat.index) || beat.line,
    })),
  };
}

/** Camera reply: one shot per beat. Lines are left alone. */
export function mergeCameraShots(treatment: ReelTreatment, text: string): ReelTreatment | null {
  const value = parseJsonObject(text);
  if (!value || !Array.isArray(value.beats)) return null;
  const shots = new Map<number, string>();
  for (const item of value.beats) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
    const record = item as Record<string, unknown>;
    const index = typeof record.index === 'number' ? record.index : Number(record.index);
    const shot = asString(record.shot);
    if (!Number.isInteger(index) || !shot) return null;
    shots.set(index, shot);
  }
  if (treatment.beats.some((beat) => !shots.has(beat.index))) return null;
  return {
    ...treatment,
    beats: treatment.beats.map((beat) => ({
      ...beat,
      shot: shots.get(beat.index) || beat.shot,
    })),
  };
}

function parseFullTreatment(value: Record<string, unknown>): ReelTreatment | null {
  const visualStyle = asString(value.visualStyle);
  if (!visualStyle) return null;
  if (!Array.isArray(value.beats) || !beatCountOk(value.beats.length)) return null;
  const beats: ReelBeat[] = [];
  for (let i = 0; i < value.beats.length; i += 1) {
    const item = value.beats[i];
    if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
    const record = item as Record<string, unknown>;
    const role = asRole(record.role);
    const line = asString(record.line);
    const shot = asString(record.shot);
    if (!role || !line || !shot) return null;
    beats.push({ index: i + 1, role, line, shot });
  }
  return {
    title: asString(value.title),
    hook: asString(value.hook),
    tone: asString(value.tone),
    visualStyle,
    picture: asPicture(value.picture),
    beats,
  };
}

export interface ReelReview {
  pass: boolean;
  treatment?: ReelTreatment;
}

/** Review reply. A corrected treatment is optional. */
export function parseReview(text: string): ReelReview | null {
  const value = parseJsonObject(text);
  if (!value || typeof value.pass !== 'boolean') return null;
  if (value.pass) return { pass: true };
  const corrected = value.treatment;
  if (corrected && typeof corrected === 'object' && !Array.isArray(corrected)) {
    const treatment = parseFullTreatment(corrected as Record<string, unknown>);
    if (treatment) return { pass: false, treatment };
  }
  return { pass: false };
}
